/**
 * TranscriptViewer - Renders meeting transcripts with speaker filters and text search.
 *
 * Handles both structured JSON (new transcripts with speaker entity linking)
 * and markdown (historical transcripts from parse_vtt_to_markdown).
 */

import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

export interface TranscriptViewerProps {
  content: string;
  contentType: string;
  orgId: number;
}

interface TranscriptSpeaker {
  name: string;
  entityType?: string;
  entityId?: string;
}

interface TranscriptSegment {
  speakerIndex: number;
  timestampSeconds: number;
  text: string;
}

interface ParsedTranscript {
  speakers: TranscriptSpeaker[];
  segments: TranscriptSegment[];
}

/** Format seconds as M:SS or H:MM:SS */
function formatSeconds(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Parse a timestamp string like "1:23:45" or "5:30" into seconds */
function parseTimestampToSeconds(timestamp: string): number {
  const parts = timestamp.split(':').map(Number);
  if (parts.length === 3) {
    return (parts[0] ?? 0) * 3600 + (parts[1] ?? 0) * 60 + (parts[2] ?? 0);
  }
  if (parts.length === 2) {
    return (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
  }
  return 0;
}

/** Parse JSON transcript content into ParsedTranscript. Throws on failure. */
function parseJsonTranscript(content: string): ParsedTranscript {
  const data = JSON.parse(content) as {
    speakers: Array<{
      name: string;
      entity_id?: string | null;
      entity_type?: string | null;
    }>;
    segments: Array<{
      speaker_index: number;
      timestamp_seconds: number;
      text: string;
    }>;
  };

  if (!Array.isArray(data.speakers) || !Array.isArray(data.segments)) {
    throw new Error('Invalid JSON transcript structure');
  }

  const speakers: TranscriptSpeaker[] = data.speakers.map(s => ({
    name: typeof s.name === 'string' ? s.name : 'Unknown',
    entityType: typeof s.entity_type === 'string' ? s.entity_type : undefined,
    entityId: typeof s.entity_id === 'string' ? s.entity_id : undefined,
  }));

  const segments: TranscriptSegment[] = data.segments
    .filter(
      s =>
        typeof s.speaker_index === 'number' &&
        typeof s.timestamp_seconds === 'number' &&
        typeof s.text === 'string'
    )
    .map(s => ({
      speakerIndex: s.speaker_index,
      timestampSeconds: s.timestamp_seconds,
      text: s.text,
    }));

  return { speakers, segments };
}

/**
 * Parse markdown transcript content using the **Speaker** (HH:MM:SS): format.
 * Throws if no segments found.
 */
function parseMarkdownTranscript(content: string): ParsedTranscript {
  const SPEAKER_RE = /\*\*(.+?)\*\*\s*\((\d{1,2}:\d{2}(?::\d{2})?)\):\s*/g;
  const speakerNameToIndex = new Map<string, number>();
  const speakers: TranscriptSpeaker[] = [];
  const segments: TranscriptSegment[] = [];

  let match: RegExpExecArray | null;
  let lastMatchEnd = 0;
  let lastSpeakerIndex = -1;
  let lastTimestampSeconds = 0;

  while ((match = SPEAKER_RE.exec(content)) !== null) {
    const speakerName = match[1] ?? '';
    const timestamp = match[2] ?? '';
    const textStart = match.index + match[0].length;

    // Text from previous match's end to this match start = previous segment text
    if (lastSpeakerIndex >= 0) {
      const text = content.slice(lastMatchEnd, match.index).trim();
      if (text) {
        segments.push({
          speakerIndex: lastSpeakerIndex,
          timestampSeconds: lastTimestampSeconds,
          text,
        });
      }
    }

    if (!speakerNameToIndex.has(speakerName)) {
      speakerNameToIndex.set(speakerName, speakers.length);
      speakers.push({ name: speakerName });
    }

    lastSpeakerIndex = speakerNameToIndex.get(speakerName) ?? 0;
    lastTimestampSeconds = parseTimestampToSeconds(timestamp);
    lastMatchEnd = textStart;
  }

  // Capture the final segment's text
  if (lastSpeakerIndex >= 0) {
    const text = content.slice(lastMatchEnd).trim();
    if (text) {
      segments.push({
        speakerIndex: lastSpeakerIndex,
        timestampSeconds: lastTimestampSeconds,
        text,
      });
    }
  }

  if (segments.length === 0) {
    throw new Error('No transcript segments found in markdown');
  }

  return { speakers, segments };
}

/** Highlight all occurrences of query in text with <mark>. Case-insensitive. */
function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query) {
    return <span>{text}</span>;
  }

  const parts: React.ReactNode[] = [];
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  let lastIndex = 0;
  let idx = lowerText.indexOf(lowerQuery, lastIndex);

  while (idx !== -1) {
    if (idx > lastIndex) {
      parts.push(text.slice(lastIndex, idx));
    }
    parts.push(
      <mark
        key={idx}
        style={{
          backgroundColor: 'var(--color-warning-100)',
          borderRadius: '2px',
        }}
      >
        {text.slice(idx, idx + query.length)}
      </mark>
    );
    lastIndex = idx + query.length;
    idx = lowerText.indexOf(lowerQuery, lastIndex);
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return <span>{parts}</span>;
}

/**
 * TranscriptViewer component.
 *
 * Parses transcript content (JSON or markdown) and renders it with:
 * - Speaker filter chips (toggle-based, scrollable for >8 speakers)
 * - Text search with inline highlighting
 * - EntityBadgeById for speakers with resolved CRM entity links
 */
export function TranscriptViewer({
  content,
  contentType,
  orgId,
}: TranscriptViewerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const firstMatchRef = useRef<HTMLDivElement | null>(null);
  const prevQueryRef = useRef('');

  // --- Parse transcript ---
  const { parsed, parseError } = useMemo<{
    parsed: ParsedTranscript | null;
    parseError: boolean;
  }>(() => {
    if (contentType === 'application/json') {
      try {
        return { parsed: parseJsonTranscript(content), parseError: false };
      } catch {
        console.warn(
          'Failed to parse JSON transcript, falling back to markdown parsing'
        );
        try {
          return {
            parsed: parseMarkdownTranscript(content),
            parseError: false,
          };
        } catch {
          return { parsed: null, parseError: true };
        }
      }
    }

    // text/markdown or text/plain
    try {
      return { parsed: parseMarkdownTranscript(content), parseError: false };
    } catch {
      return { parsed: null, parseError: true };
    }
  }, [content, contentType]);

  // --- Speaker filter state ---
  const [selectedSpeakers, setSelectedSpeakers] = useState<Set<number>>(() => {
    if (!parsed) {
      return new Set();
    }
    return new Set(parsed.speakers.map((_, i) => i));
  });

  // Reset selected speakers when parsed transcript changes
  const parsedSpeakersLength = parsed?.speakers.length ?? 0;
  useEffect(() => {
    setSelectedSpeakers(
      new Set(Array.from({ length: parsedSpeakersLength }, (_, i) => i))
    );
  }, [parsedSpeakersLength]);

  const toggleSpeaker = useCallback((index: number) => {
    setSelectedSpeakers(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  // --- Filter segments ---
  const filteredSegments = useMemo(() => {
    if (!parsed) {
      return [];
    }
    const lowerQuery = searchQuery.toLowerCase();
    return parsed.segments.filter(
      seg =>
        selectedSpeakers.has(seg.speakerIndex) &&
        (lowerQuery === '' || seg.text.toLowerCase().includes(lowerQuery))
    );
  }, [parsed, selectedSpeakers, searchQuery]);

  // --- Auto-scroll to first match when query changes ---
  useEffect(() => {
    if (
      searchQuery &&
      searchQuery !== prevQueryRef.current &&
      firstMatchRef.current
    ) {
      firstMatchRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
    prevQueryRef.current = searchQuery;
  }, [searchQuery, filteredSegments]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  // --- Error / unrecognized format ---
  if (parseError) {
    return (
      <div className='space-y-3'>
        <p
          className='text-sm font-medium'
          style={{ color: 'var(--color-text-error-primary)' }}
        >
          Transcript format not recognized
        </p>
        <div
          className='text-sm max-h-96 overflow-y-auto p-3 rounded'
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-secondary)',
          }}
        >
          <MarkdownContent content={content} size='compact' />
        </div>
      </div>
    );
  }

  if (!parsed) {
    return null;
  }

  // --- Empty transcript ---
  if (parsed.segments.length === 0) {
    return (
      <p className='text-sm' style={{ color: 'var(--color-text-tertiary)' }}>
        No transcript content available.
      </p>
    );
  }

  return (
    <div className='space-y-3'>
      {/* Speaker filter chips */}
      <div className='space-y-1.5'>
        <p className='text-xs font-medium text-[var(--color-text-secondary)]'>
          Filter by speaker:
        </p>
        <div className='overflow-x-auto'>
          <div className='flex gap-2 pb-1' style={{ minWidth: 'max-content' }}>
            {parsed.speakers.map((speaker, index) => {
              const isSelected = selectedSpeakers.has(index);
              return (
                <button
                  key={`${speaker.entityId ?? ''}-${speaker.name}`}
                  type='button'
                  aria-pressed={isSelected}
                  onClick={() => toggleSpeaker(index)}
                  className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                    isSelected
                      ? 'bg-[var(--color-brand-50)] text-[var(--color-brand-700)] border-[var(--color-brand-200)]'
                      : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] border-[var(--color-border-primary)]'
                  }`}
                >
                  {speaker.entityId ? (
                    <EntityBadgeById
                      entityId={speaker.entityId}
                      orgId={orgId}
                      variant='inline'
                      interactive={false}
                    />
                  ) : (
                    speaker.name
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Text search */}
      <input
        type='text'
        placeholder='Search transcript'
        value={searchQuery}
        onChange={handleSearchChange}
        className='w-full px-3 py-1.5 text-sm rounded border focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border-primary)',
          color: 'var(--color-text-primary)',
        }}
        aria-label='Search transcript'
      />

      {/* Segments */}
      <div
        className='space-y-3 max-h-96 overflow-y-auto p-3 rounded text-sm'
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          color: 'var(--color-text-primary)',
        }}
      >
        {filteredSegments.length === 0 ? (
          <p style={{ color: 'var(--color-text-tertiary)' }}>
            No matching segments.
          </p>
        ) : (
          filteredSegments.map((segment, i) => {
            const speaker = parsed.speakers[segment.speakerIndex];
            const isFirstMatch = i === 0 && searchQuery !== '';
            const segmentKey = `${segment.speakerIndex}-${segment.timestampSeconds}-${segment.text.slice(0, 20)}`;
            return (
              <div key={segmentKey} ref={isFirstMatch ? firstMatchRef : null}>
                <span className='mr-1'>
                  {speaker?.entityId ? (
                    <EntityBadgeById
                      entityId={speaker.entityId}
                      orgId={orgId}
                      variant='inline'
                    />
                  ) : (
                    <span className='font-semibold'>
                      {speaker?.name ?? 'Unknown'}
                    </span>
                  )}
                </span>
                <span
                  className='text-xs mr-1'
                  style={{ color: 'var(--color-text-tertiary)' }}
                >
                  ({formatSeconds(segment.timestampSeconds)}):
                </span>
                <HighlightedText text={segment.text} query={searchQuery} />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
